import { isUserAdmin } from "@/utils/_"
import { storageKeys, getLs, setLs, removeLs } from "@/utils/storage"
import { generateQuery } from "@/utils/gql"

const initialState = {
  favoriteSessions: [],
  prefs: {
    send: "enter",
    isLiveAsideOpen: true,
  },
  unreadMessagesCount: 0,
  previousToken: "",
  registrationModel: null,
  accountDeletion: null,
}

export const state = () => ({
  ...initialState,
})

export const getters = {
  user: (_state, _getters, rootState) => rootState.auth.user,

  accountDeletion: state => state.accountDeletion || null,

  favoriteSessions: state => state.favoriteSessions || [],
  // TODO: remove
  isFavorite: (_state, getters) => sessionAlias =>
    Boolean(getters.favoriteSessions.find(s => s.alias === sessionAlias)),
  isSessionFavorite: (_state, getters) => sessionAlias =>
    Boolean(getters.favoriteSessions.find(s => s.alias === sessionAlias)),

  prefs: state => state.prefs || initialState.prefs,

  isAdmin: (_state, getters) => isUserAdmin(getters.user),
  isOrganizer: (_state, getters) =>
    getters.user?.status?.alias === "organizer" && getters.isApproved,

  isImpersonalized: state => Boolean(state.previousToken),

  isApprovable: (_state, getters) => ["approvable", "approvable_paid"]
    .includes(getters.user?.status?.workflow),
  isPayable: (_state, getters) => ["paid", "approvable_paid"]
    .includes(getters.user?.status?.workflow),

  isApproved: (_state, getters) =>
    (getters.user?.states || []).includes("approved"),
  isPaid: (_state, getters) =>
    (getters.user?.states || []).includes("paid"),

  canRateContest: (_state, getters) => [
    "contestDirectorate",
    "contestRegion",
    "contestPartner",
  ].some(r => (getters.user?.roles || []).includes(r)),

  nav (_state, getters) {
    const nav = []

    nav.push({
      title: "profile",
      path: "/user/profile",
      icon: "user-pen",
      hideChildren: true,
      allowChildren: true,
    })

    nav.push({
      title: "myRequests",
      path: "/user/requests",
      icon: "paste",
      allowChildren: true,
    })

    nav.push({
      title: "events",
      path: "/user/events/my",
      icon: "calendar",
      allowChildren: true,
      // hideChildren: true,
      children: [
        {
          title: "myEvents",
          path: "/user/events/my",
        },
        {
          title: "allEvents",
          path: "/user/events/all",
        },
      ],
    })

    if (getters.canRateContest)
      nav.push({
        title: "commissionApplications",
        path: "/user/commission-requests",
        icon: "file-check-alt",
        allowChildren: true,
        hideChildren: true,
      })

    return nav
  },
}

export const mutations = {
  RESET_STATE (state) { Object.assign(state, initialState) },

  SET_FAVORITE_SESSIONS (state, sessions) { state.favoriteSessions = sessions },

  ADD_FAVORITE_SESSION (state, session) {
    state.favoriteSessions.push(session)
  },

  // TODO: remove
  DELETE_FAVORITE_SESSION (state, payload) {
    const index =
      state.favoriteSessions.findIndex(entry => entry.alias === payload)
    state.favoriteSessions.splice(index, 1)
  },
  REMOVE_FAVORITE_SESSION (state, sessionAlias) {
    const idx =
      state.favoriteSessions.findIndex(s => s.alias === sessionAlias)
    state.favoriteSessions.splice(idx, 1)
  },

  SET_PREFS (state, prefs) { state.prefs = prefs },

  SET_UNREAD_MESSAGES_COUNT (state, unreadMessagesCount) {
    state.unreadMessagesCount = unreadMessagesCount < 0 ? 0 : unreadMessagesCount
  },

  SET_PREVIOUS_TOKEN (state, token) { state.previousToken = token },

  SET_REGISTRATION_MODEL (state, registrationModel) {
    state.registrationModel = registrationModel
  },

  SET_ACCOUNT_DELETION (state, value) {
    state.accountDeletion = value
  },
}

export const actions = {
  async FETCH_FAVORITE_SESSIONS ({ commit }) {
    const [res, err] = await this.$api.program.getFavoriteSessions()
    if (err) console.error(err.response.data)
    else commit("SET_FAVORITE_SESSIONS", this.$api.helpers.getList(res))
  },

  // TODO: remove
  async ADD_FAVORITE_SESSION ({ commit }, session) {
    const [, err] = await this.$api.program.addFavoriteSession(session.alias)
    if (err) {
      console.error(err.response.data)
      return { errors: true }
    }
    commit("ADD_FAVORITE_SESSION", session)
    return { success: true }
  },
  async addFavoriteSession ({ commit }, session) {
    commit("ADD_FAVORITE_SESSION", session)
    const [, err] = await this.$api.program.addFavoriteSession(session.alias)
    if (err) {
      console.error(err.response.data)
      commit("REMOVE_FAVORITE_SESSION", sessionAlias)
    }
  },

  // TODO: remove
  async DELETE_FAVORITE_SESSION ({ commit }, sessionAlias) {
    const [, err] =
      await this.$api.program.removeFavoriteSession(sessionAlias)
    if (err) {
      console.error(err.response.data)
      return { errors: true }
    }
    commit("DELETE_FAVORITE_SESSION", sessionAlias)
    return { success: true }
  },
  async removeFavoriteSession ({ commit }, session) {
    const { alias } = session
    commit("REMOVE_FAVORITE_SESSION", alias)
    const [, err] =
      await this.$api.program.removeFavoriteSession(alias)
    if (err) {
      commit("ADD_FAVORITE_SESSION", session)
      console.error(err.response.data)
    }
  },

  FETCH_PREFS ({ commit }) {
    const prefs = getLs(storageKeys.prefs)
    if (prefs) commit("SET_PREFS", prefs)
  },

  UPDATE_PREFS ({ state, commit }, changedPrefs) {
    const newPrefs = { ...state.prefs, ...changedPrefs }
    commit("SET_PREFS", newPrefs)
    setLs(storageKeys.prefs, newPrefs)
  },

  async IMPERSONALIZE ({ commit, getters, dispatch }, token) {
    // if (!process.browser) return
    if (!getters.isImpersonalized) {
      const previousToken = this.$auth.strategy.token.get()
      if (previousToken) {
        commit("SET_PREVIOUS_TOKEN", previousToken)
        setLs(storageKeys.previousToken, previousToken)
      }
    }
    await this.$auth.setUserToken(token)
    await dispatch("fetchUser")
    // await dispatch("FETCH_FAVORITE_SESSIONS")
  },

  async EXIT_IMPERSONALIZATION ({ getters, state, commit, dispatch }) {
    // if (!process.browser) return
    if (!getters.isImpersonalized) return
    await this.$auth.setUserToken(state.previousToken)
    await dispatch("fetchUser")
    // await dispatch("FETCH_FAVORITE_SESSIONS")
    this.$router.push(this.localePath("/user"))
    commit("SET_PREVIOUS_TOKEN", "")
    removeLs(storageKeys.previousToken)
  },

  async LOGOUT ({ getters, dispatch }, { manual = false } = {}) {
    if (getters.isImpersonalized) {
      await dispatch("EXIT_IMPERSONALIZATION")
    } else {
      if (manual && this.$auth.loggedIn) {
        const [, err] = await this.$api.user.logout()
        if (err) console.error(err.message || err)
      }
      await this.$auth.logout()
      this.$router.push(this.localePath("/auth/login"))
    }
  },

  async fetchUser ({ dispatch }) {
    if (!this.$auth.loggedIn) return

    const [formRes, formErr] =
      await this.$api.form.getFormSchema("registration-form", true)

    if (formErr) {
      console.error(formErr.message)
      return
    }

    const formSchema = formRes || []

    if (!formSchema.length) return

    const query = generateQuery({
      queryName: "me",
      queryType: "find",
      entityName: "",
      formSchema,
      ignorePublic: true,
      additionalFields: {
        status: "{ id alias workflow title { current } }",
        tags: "{ group { alias }}",
        roles: "",
        role: "",
        states: "",
        profile: "{ state data }",
      },
    })

    const [meRes, meErr] = await this.$api.any.executeQuery({ query })

    if (meErr) {
      console.error(meErr.message)
      await dispatch("LOGOUT")
      return
    }

    let user = meRes || null

    if (user) {
      const attributes = user.attributes || {}
      user = { ...attributes, ...user }
      delete user.attributes

      let profile = user.profile || {}
      profile = { ...(profile.data || {}), ...profile }
      delete profile.data

      user = { ...user, profile }
    }

    this.$auth.setUser(user)
  },

  SET_ACCOUNT_DELETION ({ commit }, value) {
    commit("SET_ACCOUNT_DELETION", value)
  },
}
